import type { TrackStep } from '../bullets/patterns/PatternEmitter';
import type { EnemyType } from '../entities/enemyDefs';

/**
 * The boss pool (July 2026 rework — replaces the single Pale Warden). One
 * boss is drawn per summon from the seeded per-floor `boss` stream, so the
 * same seed + summon floor always faces the same boss. Summon-anywhere: HP
 * scales with the floor it's summoned on; loot scales harder (the third
 * descent incentive). Each phase is parallel pattern tracks; transitions
 * clear bullets, grant a breath of invulnerability, and call adds.
 *
 * Tuning stance (Sarah, July 2026): the ×2-density floors carry the heat —
 * bosses are dodge-and-punish duels. Every phase loop ends in a ≥2.5s silent
 * wait (the damage window); nothing approaches the old Warden's phase-2
 * overlap density.
 */

export interface BossTrack {
  /** Track only runs when the boss is summoned on this floor or deeper. */
  minFloor?: number;
  steps: TrackStep[];
}

export interface BossPhase {
  /** Enter when hp fraction ≤ this. */
  hpBelow: number;
  tracks: BossTrack[];
  adds: { type: EnemyType; count: number }[];
}

export interface BossDef {
  id: 'warden' | 'horror' | 'bellkeeper' | 'esoterium' | 'gravemother';
  /** Display name, ALL CAPS (HUD boss bar + summon toast). */
  name: string;
  /** Phase-change toast flavor line. */
  stirLine: string;
  /** × ENEMY_DEFS.boss.hp (850); stacks with bossHpMult(floor) and tier. */
  hpMult: number;
  speed: number;
  radius: number;
  contactDamage: number;
  phases: BossPhase[];
}

/**
 * Difficulty scaling: a floor-1 summon runs ONE pattern track at a time per
 * phase; the parallel second track (the true bullet-hell overlap) joins on
 * floor 2+. HP and loot scale separately below.
 */
export const BOSS_DEFS: readonly BossDef[] = [
  {
    // The retuned legacy boss — the legible baseline duel.
    id: 'warden',
    name: 'THE PALE WARDEN',
    stirLine: 'The Warden stirs…',
    hpMult: 1.0, speed: 60, radius: 34, contactDamage: 2,
    phases: [
      {
        hpBelow: 1.01, // opening phase
        tracks: [
          { steps: [{ loop: [{ pattern: 'twinSpiralLite' }, { wait: 3.0 }] }] },
        ],
        adds: [],
      },
      {
        hpBelow: 0.66,
        tracks: [
          { steps: [{ loop: [{ pattern: 'bossBarrage' }, { wait: 3.5 }] }] },
          { minFloor: 2, steps: [{ wait: 1.5 }, { loop: [{ pattern: 'slamAndSeek' }, { wait: 3.0 }] }] },
        ],
        adds: [{ type: 'shambler', count: 3 }],
      },
      {
        hpBelow: 0.33,
        tracks: [
          { steps: [{ loop: [{ pattern: 'bossWheelLite' }, { wait: 3.2 }] }] },
          { minFloor: 2, steps: [{ wait: 1 }, { loop: [{ pattern: 'mercyWall' }, { wait: 3.5 }] }] },
        ],
        adds: [{ type: 'watcher', count: 2 }, { type: 'shambler', count: 2 }],
      },
    ],
  },
  {
    // Swarm-lord: barely shoots — the crowd is the fight. Fast and fragile;
    // melts once the adds are down. Leech heals the adds (priority target).
    id: 'horror',
    name: 'THE CARRION HORROR',
    stirLine: 'The Horror sloughs and swells…',
    hpMult: 0.7, speed: 95, radius: 28, contactDamage: 1,
    phases: [
      {
        hpBelow: 1.01,
        tracks: [
          { steps: [{ loop: [{ pattern: 'watcherVolley' }, { wait: 2.5 }] }] },
        ],
        adds: [{ type: 'splitter', count: 2 }],
      },
      {
        hpBelow: 0.60,
        tracks: [
          { steps: [{ loop: [{ pattern: 'chanterSlam' }, { wait: 2.0 }] }] },
          { minFloor: 2, steps: [{ wait: 1.2 }, { loop: [{ pattern: 'watcherVolley' }, { wait: 3.0 }] }] },
        ],
        adds: [{ type: 'flanker', count: 2 }, { type: 'leech', count: 1 }],
      },
      {
        hpBelow: 0.30,
        tracks: [
          { steps: [{ loop: [{ pattern: 'watcherVolley' }, { wait: 2.5 }] }] },
          { minFloor: 2, steps: [{ wait: 1 }, { loop: [{ pattern: 'slamAndSeek' }, { wait: 3.0 }] }] },
        ],
        adds: [{ type: 'splitter', count: 2 }, { type: 'flanker', count: 2 }],
      },
    ],
  },
  {
    // Tanky near-stationary ring-caster: sparse gapped walls you walk
    // through, long silences between tolls.
    id: 'bellkeeper',
    name: 'THE BELLKEEPER',
    stirLine: 'The Bell tolls deeper…',
    hpMult: 1.45, speed: 34, radius: 42, contactDamage: 2,
    phases: [
      {
        hpBelow: 1.01,
        tracks: [
          { steps: [{ loop: [{ pattern: 'bellToll' }, { wait: 4.0 }] }] },
        ],
        adds: [],
      },
      {
        hpBelow: 0.62,
        tracks: [
          { steps: [{ loop: [{ pattern: 'bellToll' }, { wait: 3.2 }] }] },
          { minFloor: 2, steps: [{ wait: 1.5 }, { loop: [{ pattern: 'bruteSlam' }, { wait: 2.6 }] }] },
        ],
        adds: [{ type: 'chanter', count: 2 }],
      },
      {
        hpBelow: 0.30,
        tracks: [
          { steps: [{ loop: [{ pattern: 'bellToll' }, { wait: 2.8 }] }] },
          { minFloor: 2, steps: [{ wait: 1 }, { loop: [{ pattern: 'mercyWall' }, { wait: 3.5 }] }] },
        ],
        adds: [{ type: 'brute', count: 1 }, { type: 'chanter', count: 1 }],
      },
    ],
  },
  {
    // Duelist: telegraphed spears, then a long exposed recovery — the purest
    // dodge-and-punish. High speed makes the stock hover-strafe read darty.
    id: 'esoterium',
    name: 'THE ESOTERIUM',
    stirLine: 'The Esoterium unfolds…',
    hpMult: 0.8, speed: 120, radius: 26, contactDamage: 2,
    phases: [
      {
        hpBelow: 1.01,
        tracks: [
          { steps: [{ loop: [{ pattern: 'lanceVolley' }, { wait: 3.0 }] }] },
        ],
        adds: [],
      },
      {
        hpBelow: 0.60,
        tracks: [
          { steps: [{ loop: [{ pattern: 'lanceVolley' }, { wait: 2.6 }] }] },
          { minFloor: 2, steps: [{ wait: 1.5 }, { loop: [{ pattern: 'slamAndSeek' }, { wait: 3.0 }] }] },
        ],
        adds: [{ type: 'husk', count: 2 }],
      },
      {
        hpBelow: 0.30,
        tracks: [
          { steps: [{ loop: [{ pattern: 'lanceStorm' }, { wait: 3.0 }] }] },
          { minFloor: 2, steps: [{ wait: 1 }, { loop: [{ pattern: 'mercyWall' }, { wait: 3.5 }] }] },
        ],
        adds: [{ type: 'flanker', count: 3 }],
      },
    ],
  },
  {
    // Zoner: the player dodges geography, not aim — blooming zones and lazy
    // drifting petals.
    id: 'gravemother',
    name: 'THE GRAVEMOTHER',
    stirLine: 'The Gravemother blooms…',
    hpMult: 1.15, speed: 50, radius: 38, contactDamage: 2,
    phases: [
      {
        hpBelow: 1.01,
        tracks: [
          { steps: [{ loop: [{ pattern: 'gravebloom' }, { wait: 3.5 }] }] },
        ],
        adds: [],
      },
      {
        hpBelow: 0.65,
        tracks: [
          { steps: [{ loop: [{ pattern: 'gravebloom' }, { wait: 3.0 }] }] },
          { minFloor: 2, steps: [{ wait: 1.5 }, { loop: [{ pattern: 'chanterSlam' }, { wait: 2.4 }] }] },
        ],
        adds: [{ type: 'brute', count: 1 }],
      },
      {
        hpBelow: 0.35,
        tracks: [
          { steps: [{ loop: [{ pattern: 'gravebloom' }, { wait: 2.8 }] }] },
          { minFloor: 2, steps: [{ wait: 1 }, { loop: [{ pattern: 'bellToll' }, { wait: 4.0 }] }] },
        ],
        adds: [{ type: 'brute', count: 1 }, { type: 'chanter', count: 2 }],
      },
    ],
  },
];

/** The pattern tracks active for a boss summoned on `floor`. */
export function bossTracksForFloor(phase: BossPhase, floor: number): TrackStep[][] {
  return phase.tracks.filter((t) => floor >= (t.minFloor ?? 1)).map((t) => t.steps);
}

export function bossHpMult(floor: number): number {
  return 1 + 0.55 * (floor - 1);
}

/** Boss loot scales harder than the floor gem multiplier (design §4.3). */
export function bossGemMult(floor: number): number {
  return floor * floor;
}
